import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import HeaderPageLayout from '~/app/components-nds/common/header-page-layout'
import { HomeButton } from '~/app/components-nds/common/header-page-layout/header-buttons'
import { Footer } from '~/app/components-nds/common/footer'
import CartSelectBar from '~/app/components-nds/domestic/cart/cart-select-bar'
import CartProductSection from '~/app/components-nds/domestic/cart/cart-product-section'
import CartBannerGuide from '~/app/components-nds/domestic/cart/cart-banner-guide'
import { cn, EmptyState, Gototop } from '@yanolja-rnd/nol-design-system'
import { IcCartH72, IcErrorH72 } from '@yanolja-rnd/nol-icons'
import useGoHome from '~/app/utils/useGoHome'
import { useLoading } from '~/app/components/common/LoadingProvider'
import { DomesticCartConverterResponse } from '~/api/routes/domestic/cart/controller/type'
import OrderSummaryCard from '~/app/components-nds/domestic/cart/order-summary-card'
import PurchaseCTAButton from '~/app/components-nds/domestic/cart/purchase-cta-button'
import useIsPcWidth from '~/app/utils/useIsPcWidth'
import LoginBanner from '~/app/components-nds/common/login-banner'
import useDomesticCartDiscount from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-discount'
import useDomesticCartPrice from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-price'
import useDomesticCartOptions from '~/app/components-nds/domestic/cart/hooks/use-domestic-cart-options'
import useVisibilityChange from '~/app/utils/useVisibilityChange'
import { getYaNative } from '@yanolja-org/ya-native'
import { isNative } from '~/app/utils/typeguards'
import { sendCartPageViewEvent } from '~/app/analytics-custom-events'
import { useViewLogWithData } from '~/app/kinesis'
import { useDomesticV1CartQuery } from '~/app/react-query/domestic/cart/v1'
import { CartPageLog } from '~/app/kinesis/protobuf/clientlogs/order-misc-common-txtpb'

/**
 * New Cart Page Component
 * description: 새로운 장바구니 페이지 (신 버전)
 */

const DomesticCart = ({
  data,
  headerHeight,
}: {
  data: DomesticCartConverterResponse
  headerHeight?: number
}) => {
  const { hasOptions } = useDomesticCartOptions()

  // 모바일 고정 구매바(총 건수+금액+버튼)는 높이가 동적이라 GoToTop 위치 계산에 측정값을 쓴다
  const mobilePurchaseCtaRef = useRef<HTMLDivElement>(null)
  // GoToTop 버튼 하단 위치(px)
  const [goToTopBottom, setGoToTopBottom] = useState(24)

  const { goHome } = useGoHome()
  // 이 지면 PC 레이아웃은 pf-pc(1024)부터 시작 — JS 분기점을 CSS `pf-pc:` 와 일치시킨다
  const { isPcWidth } = useIsPcWidth(1024)

  // 쿠폰 할인
  const discountInfo = useDomesticCartDiscount({
    maxDiscountLimit: data.maxDiscountLimit,
    isMember: data.isMember,
  })
  // 총 상품 가격
  const { totalPrice } = useDomesticCartPrice()
  // 결제 예상 금액
  const expectedPayAmount = useMemo(() => {
    return totalPrice - discountInfo.maxDiscountAmount
  }, [totalPrice, discountInfo.maxDiscountAmount])

  useLayoutEffect(() => {
    // PC(pf-pc)는 하단 고정 구매바가 없어(sticky 사이드바) 24px 고정
    if (isPcWidth) {
      setGoToTopBottom(24)
      return
    }
    const cta = mobilePurchaseCtaRef.current
    if (!cta) return
    // 모바일: 고정 구매바 높이만큼 띄워 CTA와 겹치지 않게 한다 (구매바 높이는 safe-area 포함).
    // 폭/콘텐츠 변화로 구매바 높이가 바뀔 수 있어 ResizeObserver로 추적해 항상 최신값 유지.
    const updateBottom = () => setGoToTopBottom(cta.offsetHeight + 16)
    updateBottom()
    const resizeObserver = new ResizeObserver(updateBottom)
    resizeObserver.observe(cta)
    return () => resizeObserver.disconnect()
  }, [isPcWidth])

  return (
    <>
      <div className="mx-auto grid w-full max-w-pf-pc grid-cols-[1fr] pf-pc:mt-28 pf-pc:min-h-[calc(100vh-100px-28px)] pf-pc:grid-cols-[840px_360px] pf-pc:gap-x-40 pf-pc:gap-y-28 pf-pc:px-40">
        {hasOptions ? (
          <div>
            <CartSelectBar />

            <div className="mx-auto max-w-[840px] px-16 py-20 pf-pc:my-16 pf-pc:p-0">
              <CartProductSection {...data.productSection} />
            </div>

            {/* 모바일: 예약 금액, 배너+가이드, 구매하기 버튼  */}
            <div className="flex w-full flex-col gap-16 bg-static-white px-16 py-24 pf-pc:hidden">
              <OrderSummaryCard
                {...discountInfo}
                totalPrice={totalPrice}
                expectedPayAmount={expectedPayAmount}
                isNolCardLinked={data.viewer.isNolCardLinked}
              />
              <CartBannerGuide {...data.benefitBanner} />
              <PurchaseCTAButton
                ref={mobilePurchaseCtaRef}
                isMember={data.isMember}
                viewer={data.viewer}
                expectedPayAmount={expectedPayAmount}
                className={
                  'fixed bottom-0 left-0 right-0 mx-auto max-w-pf-mobile shrink-0 bg-static-white py-16 pb-[calc(12px+var(--safe-area-bottom))] shadow-[0_0_8px_0_rgba(0,0,0,0.10)] z-high pf-pc:max-w-none'
                }
              />
            </div>

            {/* PC: 배너+가이드 */}
            <div
              className={
                'hidden bg-static-white pf-pc:block pf-pc:gap-20 pf-pc:rounded-16 pf-pc:p-24'
              }
            >
              <CartBannerGuide {...data.benefitBanner} />
            </div>
          </div>
        ) : (
          // pf-pc(1024) 미만에서 흰 배경을 599 밖까지 채워 좌우 회색 미노출
          <div className="ml-[calc(50%_-_50vw)] flex min-h-[calc(100dvh_-_52px)] w-screen flex-col items-center justify-center pf-pc:ml-0 pf-pc:min-h-0 pf-pc:w-full pf-pc:bg-transparent">
            <EmptyState
              title="아직 장바구니가 비어있어요"
              description="원하는 상품을 담아두면 한 번에 예약할 수 있어요."
              icon={IcCartH72}
              buttons={[{ content: '담으러 가기', onClick: goHome }]}
            />

            {!data.isMember && (
              <LoginBanner
                className="fixed bottom-0 bg-transparent px-16 py-22 pf-pc:hidden"
                onClickLogin={async () =>
                  await CartPageLog.ButtonClickName.click({ clickName: 'CART_CLICK_TYPE_LOGIN' })
                }
              />
            )}
          </div>
        )}

        {/* NDS Gototop 기본 컨테이너는 max-w-mobile-size(500). 본문(pf-mobile 599)에 맞춰 우측 정렬 보정.
            pf-pc(1024)부터 PC라 그 전까지는 599 유지, 1024+ 풀폭. */}
        <Gototop
          className="!max-w-pf-mobile pf-pc:!max-w-[none]"
          style={{ bottom: `${goToTopBottom}px` }}
        />

        {/* PC: 예약 금액, 구매하기 버튼 */}
        <div className="hidden pf-pc:block pf-pc:h-full">
          <div
            className={'pf-pc:sticky pf-pc:flex pf-pc:flex-col pf-pc:gap-y-16'}
            style={{
              top: headerHeight !== undefined ? `${headerHeight + 28}px` : undefined, // 28은 상위 grid margin-top 값
              transition: 'top 0.3s ease-out',
            }}
          >
            {!!data.productSection.cartItems.length && (
              <OrderSummaryCard
                className={'px-16 py-24 pf-pc:rounded-16'}
                {...discountInfo}
                totalPrice={totalPrice}
                expectedPayAmount={expectedPayAmount}
                isNolCardLinked={data.viewer.isNolCardLinked}
              />
            )}
            <PurchaseCTAButton
              isMember={data.isMember}
              viewer={data.viewer}
              expectedPayAmount={expectedPayAmount}
              className={'py-24 pf-pc:rounded-16'}
            />
          </div>
        </div>
      </div>

      <Footer
        // 모바일: 고정 구매바(CTA) + GoToTop 높이만큼 하단 여백 확보해 footer 콘텐츠가 가리지 않게 함
        // (goToTopBottom = CTA높이+16, +68 = GoToTop 버튼 ~52 + 여백 16). PC는 고정바 없어 미전달.
        bottomReservePx={isPcWidth ? undefined : goToTopBottom + 68}
        className={cn(!hasOptions && 'hidden pf-pc:block')}
      />
    </>
  )
}

/**
 * New Cart Page Component
 * description: 새로운 장바구니 페이지 (신 버전)
 */
const CartPage = () => {
  const { data: cartData, isInitialLoading, isSuccess, refetch } = useDomesticV1CartQuery()

  const { allAvailableOptionIds, unavailableOptionIds, hasOptions } = useDomesticCartOptions()

  const [headerHeight, setHeaderHeight] = useState<number>()

  useLoading(isInitialLoading)

  const isEnabledNative = useMemo(() => {
    if (typeof window !== 'undefined') {
      const yaNative = getYaNative()
      return isNative(yaNative)
    }
    return false
  }, [])

  useVisibilityChange({
    isActive: isEnabledNative,
    onVisible: refetch,
  })

  useViewLogWithData({
    data: cartData?.clientLog ? { ...cartData.clientLog.pageView } : undefined,
    sendLog: logData => {
      sendCartPageViewEvent({
        ...logData,
        rtb: {
          productIds: [...allAvailableOptionIds, ...unavailableOptionIds],
          hashedMemberNo: cartData?.viewer.hashedMemberNo ?? '',
        },
      })
    },
  })

  return (
    <HeaderPageLayout
      mobileLeftSlotType="back"
      mobileTitle="장바구니"
      mobileRightSlot={<HomeButton />}
      pcHeaderType="full"
      isCartActive
      pageName="PAGE_NAME_CART"
      hasBottomCta
      headerScrollEffect={false}
      isShowFooter={false}
      onHeaderHeightChange={setHeaderHeight}
    >
      {/* 상품 있을떄 / 빈 에러 화면인 경우 분리 */}
      <div
        className={cn(
          'mx-auto max-w-pf-mobile bg-fill-neutral-weak pf-pc:min-h-screen pf-pc:min-w-pf-pc pf-pc:max-w-none',
          isSuccess && hasOptions ? 'min-h-screen' : 'min-h-[calc(100dvh_-_52px)]'
        )}
      >
        {!isInitialLoading &&
          (isSuccess ? (
            <DomesticCart data={cartData} headerHeight={headerHeight} />
          ) : (
            // pf-pc 빈 화면과 동일 처리
            <div className="ml-[calc(50%_-_50vw)] flex min-h-[calc(100dvh_-_52px)] w-screen items-center justify-center pf-pc:mx-auto pf-pc:h-[calc(100vh-100px)] pf-pc:min-h-0 pf-pc:w-pf-pc">
              <EmptyState
                title="화면을 불러오지 못했어요"
                icon={IcErrorH72}
                buttons={[{ content: '다시 시도하기', onClick: () => refetch() }]}
              />
            </div>
          ))}
      </div>
    </HeaderPageLayout>
  )
}

export default CartPage
